import React, { useState, useEffect } from 'react';
import { 
  X, Save, Plus, Trash2, ChevronDown, Calendar, 
  ArrowLeft, Clock, Calendar as CalendarIcon, 
  MoreVertical, Info, CheckCircle2, LayoutGrid,
  Divide, Settings, Eye, Edit3, Send
} from 'lucide-react';
import { useData } from '../../contexts/DataContext';
import EXAM_API from '../../api/services/examApi';
import SUBJECT_API from '../../api/services/subjectApi';
import toast from 'react-hot-toast';
import BrandedButton from '../../ui/BrandedButton';
import Loader from '../../ui/Loader';

const formatDateForInput = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return isNaN(date.getTime()) ? '' : date.toISOString().split('T')[0];
};

const ExamForm = ({ selectedClass, exam, onBack, onSubmit }) => {
  const { classes: apiClasses, currentAcademicYear } = useData();
  const [formData, setFormData] = useState({
    examName: '',
    examType: '',
    selectedClassId: selectedClass?._id || '',
    sectionIds: selectedClass?.sectionId ? [selectedClass.sectionId] : [],
    academicYear: currentAcademicYear || '',
    isActive: true,
    description: '',
    passingPercentage: 33
  });

  const [subjects, setSubjects] = useState([]);
  const [availableSubjects, setAvailableSubjects] = useState([]);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [expandedBreakdowns, setExpandedBreakdowns] = useState({});
  const [expandedSubjects, setExpandedSubjects] = useState({ 0: true });
  const [showAdvanced, setShowAdvanced] = useState(false);

  const [gradingSystem, setGradingSystem] = useState([
    { grade: 'A+', minPercentage: 90, maxPercentage: 100, description: 'Excellent Performance' },
    { grade: 'A', minPercentage: 80, maxPercentage: 89, description: 'Very Good' },
    { grade: 'B', minPercentage: 60, maxPercentage: 79, description: 'Good' },
    { grade: 'C', minPercentage: 40, maxPercentage: 59, description: 'Average' },
    { grade: 'D', minPercentage: 33, maxPercentage: 39, description: 'Pass' },
    { grade: 'F', minPercentage: 0, maxPercentage: 32, description: 'Fail' },
  ]);

  const [notificationOptions, setNotificationOptions] = useState({
    mode: 'none', // 'none' | 'now' | 'schedule'
    targetAudience: 'parent',
    scheduledDate: '',
    scheduledTime: '10:00'
  });

  useEffect(() => {
    if (exam) {
      const firstClass = exam.classes?.[0];
      if (firstClass) {
        setFormData({
          examName: exam.examName || '',
          examType: exam.examType || '',
          selectedClassId: firstClass.unitId?._id || firstClass.unitId || '',
          sectionIds: firstClass.sectionIds?.map(s => s._id || s) || (firstClass.sectionId ? [firstClass.sectionId] : []),
          academicYear: exam.academicYear || currentAcademicYear,
          isActive: exam.isActive !== undefined ? exam.isActive : true,
          description: exam.description || '',
          passingPercentage: exam.passingPercentage || 33
        });

        const subjectList = firstClass.subjects || [];
        setSubjects(subjectList.map(s => ({
          subjectId: s.subjectId?._id || s.subjectId,
          subjectName: s.subjectName,
          totalMarks: s.totalMarks || 100,
          passingMarks: s.passingMarks || 33,
          examDate: formatDateForInput(s.examDate),
          startTime: s.startTime || '09:00',
          endTime: s.endTime || '12:00',
          markComponents: s.markComponents || s.markComponents || []
        })));
      }
      if (exam.gradingSystem) setGradingSystem(exam.gradingSystem);
    }
  }, [exam, currentAcademicYear]);

  useEffect(() => {
    if (formData.selectedClassId) {
      SUBJECT_API.getSubjectsByClass(formData.selectedClassId, formData.academicYear)
        .then(res => setAvailableSubjects(res.data || []))
        .catch(() => setAvailableSubjects([]));
    }
  }, [formData.selectedClassId, formData.academicYear]);

  const handleAddSubject = () => {
    if (!formData.selectedClassId) {
      toast.error('Please select a class first');
      return;
    }
    const nextIdx = subjects.length;
    setSubjects([...subjects, {
      subjectId: '',
      subjectName: '',
      totalMarks: 100,
      passingMarks: 33,
      examDate: '',
      startTime: '09:00',
      endTime: '12:00',
      markComponents: []
    }]);
    setExpandedSubjects(prev => ({ ...prev, [nextIdx]: true }));
  };

  const toggleSubject = (index) => {
    setExpandedSubjects(prev => ({ ...prev, [index]: !prev[index] }));
  };

  const handleSubjectChange = (index, field, value) => {
    const updated = [...subjects];
    updated[index][field] = value;
    
    if (field === 'subjectId') {
      const selected = availableSubjects.find(s => s._id === value);
      if (selected) updated[index].subjectName = selected.subjectName;
    }
    setSubjects(updated);
  };

  const handleRemoveSubject = (index) => {
    setSubjects(subjects.filter((_, i) => i !== index));
  };

  const handleAddMarkComponent = (subjectIndex) => {
    const updated = [...subjects];
    updated[subjectIndex].markComponents.push({ name: '', maxMarks: 0, minMarks: 0 });
    setSubjects(updated);
    setExpandedBreakdowns(prev => ({ ...prev, [subjectIndex]: true }));
  };

  const handleMarkComponentChange = (subjectIndex, compIndex, field, value) => {
    const updated = [...subjects];
    updated[subjectIndex].markComponents[compIndex][field] = value;
    setSubjects(updated);
  };

  const handleRemoveMarkComponent = (subjectIndex, compIndex) => {
    const updated = [...subjects];
    updated[subjectIndex].markComponents.splice(compIndex, 1);
    setSubjects(updated);
  };

  const toggleBreakdown = (index) => {
    setExpandedBreakdowns(prev => ({ ...prev, [index]: !prev[index] }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.examName.trim()) newErrors.examName = 'Required';
    if (!formData.examType) newErrors.examType = 'Required';
    if (!formData.selectedClassId) newErrors.selectedClassId = 'Required';
    
    // Check if class uses sections
    const selectedUnit = apiClasses.find(c => (c._id || c.id) === formData.selectedClassId);
    if (selectedUnit?.sections?.length > 0 && (!formData.sectionIds || formData.sectionIds.length === 0)) {
        newErrors.sectionIds = 'Select at least one section';
    }

    if (subjects.length === 0) newErrors.subjects = 'Add at least one subject';
    
    subjects.forEach((s, i) => {
      if (!s.subjectId) newErrors[`subject_${i}`] = 'Select subject';
      if (!s.examDate) newErrors[`date_${i}`] = 'Required';
    });
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    if (!validateForm()) {
        toast.error('Please fix all errors');
        return;
    }

    setIsSubmitting(true);
    try {
      const payload = {
        examName: formData.examName,
        examType: formData.examType,
        classes: [{
          unitId: formData.selectedClassId,
          sectionIds: formData.sectionIds,
          subjects: subjects.map(s => ({
            subjectId: s.subjectId,
            subjectName: s.subjectName,
            subjectCode: s.subjectCode || s.subjectName.substring(0, 3).toUpperCase(),
            totalMarks: s.totalMarks,
            passingMarks: s.passingMarks,
            examDate: s.examDate ? new Date(s.examDate).toISOString() : null,
            startTime: s.startTime,
            endTime: s.endTime,
            markComponents: s.markComponents
          }))
        }],
        academicYear: formData.academicYear,
        isActive: formData.isActive,
        description: formData.description,
        passingPercentage: parseInt(formData.passingPercentage) || 33,
        gradingSystem: gradingSystem.map(g => ({
          grade: g.grade,
          minPercentage: parseInt(g.minPercentage) || 0,
          maxPercentage: parseInt(g.maxPercentage) || 100,
          description: g.description
        }))
      };

      let response;
      if (exam?._id) {
        response = await EXAM_API.updateExam(exam._id, payload);
        toast.success('Exam updated successfully');
      } else {
        response = await EXAM_API.createExam(payload);
        toast.success('Exam created successfully');
      }
      const examId = exam?._id || response?.data?._id || response?.data?.exam?._id;

      if (examId && notificationOptions.mode !== 'none') {
        try {
          let noticePayload = {
            sendWhatsapp: true,
            targetAudience: notificationOptions.targetAudience
          };

          if (notificationOptions.mode === 'schedule') {
            if (!notificationOptions.scheduledDate || !notificationOptions.scheduledTime) {
                toast.error("Notice scheduling incomplete. Date/Time missing.");
            } else {
                const combinedDate = new Date(`${notificationOptions.scheduledDate}T${notificationOptions.scheduledTime}`);
                noticePayload.scheduledDate = combinedDate.toISOString();
            }
          }

          await EXAM_API.createExamNotice(examId, noticePayload);
          toast.success(notificationOptions.mode === 'now' ? 'Notice sent via WhatsApp!' : 'Notice scheduled successfully!');
        } catch (err) {
          console.error(err);
          toast.error("Exam saved, but notification failed.");
        }
      }

      onSubmit();
    } catch (error) {
      toast.error(error.message || 'Failed to save exam');
    } finally {
      setIsSubmitting(false);
    }
  };

  const getTotalComponentMarks = (subject) => {
    return subject.markComponents.reduce((sum, comp) => sum + (parseInt(comp.maxMarks) || 0), 0);
  };

  return (
    <div className="mx-auto max-w-7xl py-6">
      {/* PROCESSING LOADER */}
      {isSubmitting && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-white/80 backdrop-blur-sm">
          <div className="bg-white p-8 rounded-lg shadow-xl border border-gray-200 flex flex-col items-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mb-4"></div>
            <p className="text-gray-700 font-medium text-sm">Saving exam...</p>
          </div>
        </div>
      )}

      {/* HEADER SECTION */}
      <div className="sticky top-0 z-10 bg-gray-50 pb-4">
        <div className="flex items-center gap-4 mb-6 px-4">
          <button 
            onClick={onBack}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft size={20} className="text-gray-600" />
          </button>
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <Calendar size={20} className="text-gray-900" />
              <h1 className="text-xl font-semibold text-gray-900">
                {exam ? "Edit Exam" : "Create New Exam"}
              </h1>
            </div>
            <p className="text-sm text-gray-500">Configure examination details and schedule</p>
          </div>
          
          <BrandedButton
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="flex items-center gap-2"
          >
            <Save size={16} />
            <span>{exam ? 'Update Exam' : 'Create Exam'}</span>
          </BrandedButton>
        </div>
      </div>

      <div className="space-y-0">
        {/* BASIC INFORMATION */}
        <div className="bg-white border-t border-b border-gray-200">
          <div className="px-5 py-3 bg-gray-50">
            <h3 className="text-xs font-semibold text-gray-900 uppercase tracking-wide">Basic Information</h3>
          </div>
          
          <div className="p-4 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div className="space-y-2">
                <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Exam Name *</label>
                <input
                  type="text"
                  value={formData.examName}
                  onChange={e => setFormData({...formData, examName: e.target.value})}
                  className="w-full bg-white border border-gray-300 rounded-md px-4 py-2.5 text-sm text-gray-900 outline-none focus:border-gray-900 focus:ring-1 focus:ring-gray-900 transition-all"
                  placeholder="e.g., Mid-Term Examination"
                />
                {errors.examName && <p className="text-xs text-red-600">{errors.examName}</p>}
              </div>

              <div className="space-y-2">
                <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Exam Type *</label>
                <select
                  value={formData.examType}
                  onChange={e => setFormData({...formData, examType: e.target.value})}
                  className="w-full bg-white border border-gray-300 rounded-md px-4 py-2.5 text-sm text-gray-900 outline-none focus:border-gray-900 focus:ring-1 focus:ring-gray-900 transition-all"
                >
                  <option value="">Select Type</option>
                  <option value="Unit Test">Unit Test</option>
                  <option value="Mid Term">Mid Term</option>
                  <option value="Final">Final</option>
                  <option value="Periodic Test">Periodic Test</option>
                </select>
                {errors.examType && <p className="text-xs text-red-600">{errors.examType}</p>}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div className="space-y-2">
                <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Class *</label>
                <select
                  value={formData.selectedClassId}
                  onChange={e => setFormData({...formData, selectedClassId: e.target.value, sectionIds: []})}
                  className="w-full bg-white border border-gray-300 rounded-md px-4 py-2.5 text-sm text-gray-900 outline-none focus:border-gray-900 focus:ring-1 focus:ring-gray-900 transition-all"
                >
                  <option value="">Select Class</option>
                  {apiClasses.map(cls => (
                    <option key={cls._id} value={cls._id}>{cls.unitName}</option>
                  ))}
                </select>
                {errors.selectedClassId && <p className="text-xs text-red-600">{errors.selectedClassId}</p>}
              </div>

              {/* SECTION SELECTION */}
              {formData.selectedClassId && apiClasses.find(c => (c._id || c.id) === formData.selectedClassId)?.sections?.length > 0 && (
                 <div className="col-span-1 md:col-span-2 space-y-2">
                    <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                      Target Sections * <span className="text-[10px] lowercase italic">(Select at least one)</span>
                    </label>
                    <div className="flex flex-wrap gap-3">
                       {apiClasses.find(c => (c._id || c.id) === formData.selectedClassId).sections.map(sec => (
                          <button
                            key={sec._id}
                            type="button"
                            onClick={() => {
                              const newIds = formData.sectionIds.includes(sec._id)
                                ? formData.sectionIds.filter(id => id !== sec._id)
                                : [...formData.sectionIds, sec._id];
                              setFormData({...formData, sectionIds: newIds});
                            }}
                            className={`px-4 py-2 rounded-lg text-xs font-bold uppercase border transition-all ${
                              formData.sectionIds.includes(sec._id)
                                ? 'bg-gray-900 border-gray-900 text-white shadow-md'
                                : 'bg-white border-gray-200 text-gray-600 hover:border-gray-400'
                            }`}
                          >
                            {sec.name || sec.sectionName}
                          </button>
                       ))}
                    </div>
                    {errors.sectionIds && <p className="text-xs text-red-600 font-medium">{errors.sectionIds}</p>}
                 </div>
              )}

              <div className="space-y-2">
                <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Academic Year</label>
                <input
                  type="text"
                  value={formData.academicYear}
                  readOnly
                  className="w-full bg-gray-50 border border-gray-300 rounded-md px-4 py-2.5 text-sm text-gray-600"
                />
              </div>

              <div className="space-y-2">
                <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Min. Pass % (Global) *</label>
                <input
                  type="number"
                  value={formData.passingPercentage}
                  onChange={e => setFormData({...formData, passingPercentage: e.target.value})}
                  onWheel={(e) => e.target.blur()} 
                  className="w-full bg-white border border-gray-300 rounded-md px-4 py-2.5 text-sm text-gray-900 outline-none focus:border-gray-900 focus:ring-1 focus:ring-gray-900 transition-all text-center font-bold"
                  placeholder="33"
                  min="0"
                  max="100"
                />
              </div>
            </div>
          </div>
        </div>

        {/* EXAM SCHEDULE */}
        <div className="bg-white border-b border-gray-200">
          <div className="px-5 py-3 bg-gray-50 flex items-center justify-between">
            <h3 className="text-xs font-semibold text-gray-900 uppercase tracking-wide">Exam Schedule</h3>
            <BrandedButton 
              type="button" 
              onClick={handleAddSubject}
              className="flex items-center gap-2"
              variant="primary"
            >
              <Plus size={14} />
              Add Subject
            </BrandedButton>
          </div>
          
          <div className="p-4">
            {subjects.length === 0 ? (
              <div 
                onClick={handleAddSubject}
                className="border-2 border-dashed border-gray-300 rounded-lg py-12 text-center hover:bg-gray-50 transition-colors cursor-pointer"
              >
                <Plus size={32} className="text-gray-400 mx-auto mb-3" />
                <p className="text-sm text-gray-500">No subjects added yet. Click to add a subject.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {subjects.map((subject, idx) => {
                  const totalBreakdown = getTotalComponentMarks(subject);
                  const isBreakdownValid = totalBreakdown <= subject.totalMarks;
                  const isExpanded = expandedBreakdowns[idx];
                  
                  return (
                    <div key={idx} className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm transition-all duration-300">
                      {/* SUBJECT HEADER (Accordion Header) */}
                      <div 
                        onClick={() => toggleSubject(idx)}
                        className={`px-5 py-4 flex items-center justify-between cursor-pointer transition-colors ${expandedSubjects[idx] ? 'bg-gray-50 border-b border-gray-200' : 'hover:bg-gray-50'}`}
                      >
                         <div className="flex items-center gap-4">
                            <div className={`h-10 w-10 rounded-xl flex items-center justify-center font-bold text-sm transition-all ${expandedSubjects[idx] ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-400'}`}>
                               {idx + 1}
                            </div>
                            <div>
                               <h4 className="font-bold text-gray-900 leading-tight">
                                  {subject.subjectName || "Select a Subject..."}
                               </h4>
                               <div className="flex items-center gap-3 mt-1">
                                  <div className="flex items-center gap-1 text-[10px] font-bold text-gray-400 uppercase tracking-wider">
                                     <LayoutGrid size={10} />
                                     {subject.totalMarks} MARKS
                                  </div>
                                  <div className="flex items-center gap-1 text-[10px] font-bold text-gray-400 uppercase tracking-wider">
                                     <Calendar size={10} />
                                     {subject.examDate ? new Date(subject.examDate).toLocaleDateString(undefined, { day: 'numeric', month: 'short' }) : "DATE PENDING"}
                                  </div>
                                </div>
                            </div>
                         </div>
                         <div className="flex items-center gap-3">
                            <button 
                              type="button" 
                              onClick={(e) => { e.stopPropagation(); handleRemoveSubject(idx); }} 
                              className="p-2 text-gray-300 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all"
                            >
                              <Trash2 size={18} />
                            </button>
                            <ChevronDown size={20} className={`text-gray-400 transition-transform duration-300 ${expandedSubjects[idx] ? 'rotate-180' : ''}`} />
                         </div>
                      </div>

                      {expandedSubjects[idx] && (
                        <div className="p-5 space-y-5 animate-in fade-in slide-in-from-top-1 duration-200">
                          {/* ROW 1: Subject, Total Marks, Pass Marks */}
                          <div className="grid grid-cols-1 md:grid-cols-12 gap-5">
                             <div className="md:col-span-6 space-y-1.5">
                                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Subject *</label>
                                <div className="relative">
                                    <select
                                      value={subject.subjectId}
                                      onChange={e => handleSubjectChange(idx, 'subjectId', e.target.value)}
                                      className="w-full bg-white border-2 border-gray-200 rounded-lg px-4 py-3 text-sm font-bold text-gray-900 outline-none focus:border-[var(--primary-color)] transition-all appearance-none"
                                    >
                                      <option value="">Select Subject</option>
                                      {availableSubjects.map(s => (
                                        <option key={s._id} value={s._id}>{s.subjectName}</option>
                                      ))}
                                    </select>
                                    <ChevronDown size={16} className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                                </div>
                             </div>
                             <div className="md:col-span-3 space-y-1.5">
                                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Total Marks</label>
                                <input
                                  type="number"
                                  value={subject.totalMarks}
                                  onChange={e => handleSubjectChange(idx, 'totalMarks', parseInt(e.target.value) || 0)}
                                  onWheel={(e) => e.target.blur()} 
                                  className="w-full bg-white border-2 border-gray-200 rounded-lg px-4 py-3 text-sm font-bold text-gray-900 text-center outline-none focus:border-[var(--primary-color)] transition-all"
                                />
                             </div>
                             <div className="md:col-span-3 space-y-1.5">
                                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Pass Marks</label>
                                <input
                                  type="number"
                                  value={subject.passingMarks}
                                  onChange={e => handleSubjectChange(idx, 'passingMarks', parseInt(e.target.value) || 0)}
                                  onWheel={(e) => e.target.blur()} 
                                  className="w-full bg-white border-2 border-gray-200 rounded-lg px-4 py-3 text-sm font-bold text-gray-900 text-center outline-none focus:border-[var(--primary-color)] transition-all"
                                />
                             </div>
                          </div>

                          {/* ROW 2: Date, Start, End */}
                          <div className="grid grid-cols-1 md:grid-cols-12 gap-5 items-end">
                             <div className="md:col-span-4 space-y-1.5">
                                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Exam Date *</label>
                                <div className="relative">
                                    <input
                                      type="date"
                                      value={subject.examDate}
                                      onChange={e => handleSubjectChange(idx, 'examDate', e.target.value)}
                                      className="w-full bg-white border-2 border-gray-200 rounded-lg px-4 py-3 text-sm font-medium text-gray-900 outline-none focus:border-[var(--primary-color)] transition-all uppercase"
                                    />
                                </div>
                             </div>
                             <div className="md:col-span-4 space-y-1.5">
                                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Start Time</label>
                                <div className="relative">
                                    <input
                                      type="time"
                                      value={subject.startTime}
                                      onChange={e => handleSubjectChange(idx, 'startTime', e.target.value)}
                                      className="w-full bg-white border-2 border-gray-200 rounded-lg px-4 py-3 text-sm font-medium text-gray-900 outline-none focus:border-[var(--primary-color)] transition-all"
                                    />
                                    <Clock size={16} className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                                </div>
                             </div>
                             <div className="md:col-span-4 space-y-1.5">
                                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">End Time</label>
                                <div className="relative">
                                    <input
                                      type="time"
                                      value={subject.endTime}
                                      onChange={e => handleSubjectChange(idx, 'endTime', e.target.value)}
                                      className="w-full bg-white border-2 border-gray-200 rounded-lg px-4 py-3 text-sm font-medium text-gray-900 outline-none focus:border-[var(--primary-color)] transition-all"
                                    />
                                    <Clock size={16} className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                                </div>
                             </div>
                          </div>

                          {/* ROW 3: Assessment Breakdown Header & Add Button */}
                          <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                             <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest leading-none">Assessment Breakdown</label>
                             <button 
                                type="button" 
                                onClick={() => handleAddMarkComponent(idx)} 
                                className="text-xs font-bold text-[var(--primary-color)] hover:underline flex items-center gap-1"
                             >
                                <Plus size={12} /> Add Component
                             </button>
                          </div>

                          {/* ROW 4: Breakdown Components */}
                          <div className="space-y-3">
                            {subject.markComponents.length === 0 ? (
                              <p className="text-[10px] text-gray-400 font-medium italic">No components added. Total marks will be treated as single unit.</p>
                            ) : (
                               subject.markComponents.map((comp, cIdx) => (
                                <div key={cIdx} className="flex items-center gap-3 animate-in slide-in-from-top-1">
                                  <input
                                    type="text"
                                    placeholder="Component (e.g. Theory)"
                                    value={comp.name}
                                    onChange={e => handleMarkComponentChange(idx, cIdx, 'name', e.target.value)}
                                    className="flex-1 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-sm font-medium text-gray-900 focus:border-[var(--primary-color)] outline-none transition-all"
                                  />
                                  <div className="relative w-28">
                                      <input
                                        type="number"
                                        placeholder="Marks"
                                        value={comp.maxMarks}
                                        onChange={e => handleMarkComponentChange(idx, cIdx, 'maxMarks', parseInt(e.target.value) || 0)}
                                        onWheel={(e) => e.target.blur()} 
                                        className="w-full bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-sm font-bold text-gray-900 text-center focus:border-[var(--primary-color)] outline-none transition-all"
                                      />
                                      <span className="absolute right-8 top-1/2 -translate-y-1/2 text-xs text-gray-400 font-medium">/</span>
                                  </div>
                                  <button 
                                    onClick={() => handleRemoveMarkComponent(idx, cIdx)} 
                                    className="p-2 text-gray-300 hover:text-red-500 transition-colors"
                                  >
                                    <X size={16} />
                                  </button>
                                </div>
                              ))
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* ADVANCED SETTINGS */}
        <div className="bg-white border-b border-gray-200">
          <div 
            className="px-6 py-4 bg-gray-50 flex items-center justify-between cursor-pointer hover:bg-gray-100 transition-colors"
            onClick={() => setShowAdvanced(!showAdvanced)}
          >
            <h3 className="text-xs font-semibold text-gray-900 uppercase tracking-wide">Advanced Settings</h3>
            <ChevronDown className={`text-gray-600 transition-transform ${showAdvanced ? 'rotate-180' : ''}`} size={20} />
          </div>

          {showAdvanced && (
            <div className="p-6 space-y-6">
              {/* Visibility Checkbox */}
              <div className="flex items-start gap-3">
                <input 
                  type="checkbox" 
                  id="publishExam"
                  checked={formData.isActive}
                  onChange={e => setFormData({...formData, isActive: e.target.checked})}
                  className="mt-1 w-4 h-4 text-teal-600 bg-white border-gray-300 rounded focus:ring-teal-500"
                />
                <label htmlFor="publishExam" className="text-sm text-gray-700">
                  Publish exam (make it visible to students)
                </label>
              </div>

              {/* Grading System Table */}
              <div className="space-y-3">
                <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wide">Grading System (Editable)</h4>
                
                <div className="border border-gray-200 rounded-md overflow-hidden">
                  {/* Table Header */}
                  <div className="grid grid-cols-12 gap-4 bg-gray-50 px-4 py-3 border-b border-gray-200">
                    <div className="col-span-2 text-xs font-medium text-gray-700"></div>
                    <div className="col-span-2 text-xs font-medium text-gray-700 text-center">≥</div>
                    <div className="col-span-2 text-xs font-medium text-gray-700 text-center">to</div>
                    <div className="col-span-1 text-xs font-medium text-gray-700 text-center">%</div>
                    <div className="col-span-5 text-xs font-medium text-gray-700"></div>
                  </div>
                  
                  {/* Table Rows */}
                  {gradingSystem.map((g, i) => (
                    <div key={i} className="grid grid-cols-12 gap-4 px-4 py-3 border-b border-gray-200 last:border-b-0 hover:bg-gray-50 items-center">
                      <div className="col-span-2">
                        <div 
                          className="w-10 h-10 rounded flex items-center justify-center text-sm font-bold text-white shadow-sm"
                          style={{ backgroundColor: 'var(--primary-color)' }}
                        >
                          {g.grade}
                        </div>
                      </div>
                      <div className="col-span-2">
                        <input 
                          type="number" 
                          value={g.minPercentage}
                          onChange={(e) => {
                            const updated = [...gradingSystem];
                            updated[i].minPercentage = parseInt(e.target.value) || 0;
                            setGradingSystem(updated);
                          }}
                          onWheel={(e) => e.target.blur()} 
                          className="w-full bg-white border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-900 text-center"
                        />
                      </div>
                      <div className="col-span-2">
                        <input 
                          type="number" 
                          value={g.maxPercentage}
                          onChange={(e) => {
                            const updated = [...gradingSystem];
                            updated[i].maxPercentage = parseInt(e.target.value) || 0;
                            setGradingSystem(updated);
                          }}
                          onWheel={(e) => e.target.blur()} 
                          className="w-full bg-white border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-900 text-center"
                        />
                      </div>
                      <div className="col-span-1 text-center text-sm text-gray-500">%</div>
                      <div className="col-span-5">
                        <input 
                          type="text" 
                          value={g.description}
                          onChange={(e) => {
                            const updated = [...gradingSystem];
                            updated[i].description = e.target.value;
                            setGradingSystem(updated);
                          }}
                          className="w-full bg-white border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-900"
                          placeholder="Description"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* DISTRIBUTION & NOTIFICATION */}
        <div className="bg-white border-b border-gray-200">
          <div className="px-5 py-3 bg-gray-50 border-t border-gray-200">
            <h3 className="text-xs font-semibold text-gray-900 uppercase tracking-wide">Distribution & Notification</h3>
          </div>
          <div className="p-6 space-y-6">
             <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {/* Audience */}
                <div className="space-y-3">
                   <label className="text-xs font-medium text-gray-500 uppercase tracking-wide block">Target Audience</label>
                   <div className="flex flex-col gap-2">
                      <label className="flex items-center p-3 rounded-lg border border-gray-200 cursor-pointer hover:bg-gray-50 transition-all">
                        <input 
                          type="radio" 
                          name="audience"
                          checked={notificationOptions.targetAudience === 'student'}
                          onChange={() => setNotificationOptions({...notificationOptions, targetAudience: 'student'})}
                          className="w-4 h-4 text-[var(--primary-color)] focus:ring-[var(--primary-color)] border-gray-300"
                        />
                        <span className="ml-3 text-sm font-medium text-gray-900">Students Only</span>
                      </label>
                      <label className="flex items-center p-3 rounded-lg border border-gray-200 cursor-pointer hover:bg-gray-50 transition-all">
                        <input 
                          type="radio" 
                          name="audience"
                          checked={notificationOptions.targetAudience === 'parent'}
                          onChange={() => setNotificationOptions({...notificationOptions, targetAudience: 'parent'})}
                          className="w-4 h-4 text-[var(--primary-color)] focus:ring-[var(--primary-color)] border-gray-300"
                        />
                        <span className="ml-3 text-sm font-medium text-gray-900">Parents Only</span>
                      </label>
                      <label className="flex items-center p-3 rounded-lg border border-gray-200 cursor-pointer hover:bg-gray-50 transition-all">
                        <input 
                          type="radio" 
                          name="audience"
                          checked={notificationOptions.targetAudience === 'both'}
                          onChange={() => setNotificationOptions({...notificationOptions, targetAudience: 'both'})}
                          className="w-4 h-4 text-[var(--primary-color)] focus:ring-[var(--primary-color)] border-gray-300"
                        />
                        <span className="ml-3 text-sm font-medium text-gray-900">Both (Parents & Students)</span>
                      </label>
                   </div>
                </div>

                {/* Schedule Mode */}
                <div className="space-y-3">
                   <label className="text-xs font-medium text-gray-500 uppercase tracking-wide block">Notification Timing</label>
                   <div className="grid grid-cols-3 gap-2 p-1 bg-gray-100 rounded-xl">
                      <button
                         type="button"
                         onClick={() => setNotificationOptions({...notificationOptions, mode: 'none'})}
                         className={`py-2 px-3 rounded-lg text-xs font-bold transition-all flex items-center justify-center gap-2 ${notificationOptions.mode === 'none' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                      >
                         <Calendar size={12} /> None
                      </button>
                      <button
                         type="button"
                         onClick={() => setNotificationOptions({...notificationOptions, mode: 'now'})}
                         className={`py-2 px-3 rounded-lg text-xs font-bold transition-all flex items-center justify-center gap-2 ${notificationOptions.mode === 'now' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                      >
                         <Send size={12} /> Send Now
                      </button>
                      <button
                         type="button"
                         onClick={() => setNotificationOptions({...notificationOptions, mode: 'schedule'})}
                         className={`py-2 px-3 rounded-lg text-xs font-bold transition-all flex items-center justify-center gap-2 ${notificationOptions.mode === 'schedule' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                      >
                         <Clock size={12} /> Schedule
                      </button>
                   </div>

                   {notificationOptions.mode === 'schedule' && (
                     <div className="grid grid-cols-2 gap-3 mt-4 animate-in slide-in-from-top-2">
                        <div className="space-y-1.5">
                           <label className="text-[10px] uppercase font-bold text-gray-500 tracking-wider">Date</label>
                           <input
                              type="date"
                              className="w-full bg-white border border-gray-200 p-2 rounded-lg text-xs font-medium focus:border-[var(--primary-color)] outline-none"
                              min={new Date().toISOString().split('T')[0]}
                              value={notificationOptions.scheduledDate}
                              onChange={e => setNotificationOptions({...notificationOptions, scheduledDate: e.target.value})}
                           />
                        </div>
                        <div className="space-y-1.5">
                           <label className="text-[10px] uppercase font-bold text-gray-500 tracking-wider">Time</label>
                           <input
                              type="time"
                              className="w-full bg-white border border-gray-200 p-2 rounded-lg text-xs font-medium focus:border-[var(--primary-color)] outline-none"
                              value={notificationOptions.scheduledTime}
                              onChange={e => setNotificationOptions({...notificationOptions, scheduledTime: e.target.value})}
                           />
                        </div>
                     </div>
                   )}
                </div>
             </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default ExamForm;
